import { css, cx } from "styled-system/css";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import type { AppointmentStatus, PaymentStatus } from "@prisma/client";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { labelAppointmentStatus, labelPaymentStatus } from "@/lib/dashboard-labels";
import { MercadoPagoForm } from "@/components/dashboard/mercadopago-form";
import { CancellationPolicyForm } from "@/components/dashboard/cancellation-policy-form";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PaymentsFiltersPanel,
  DEFAULT_PAYMENTS_TABLE_FILTERS,
  type PaymentsTableFilters,
} from "@/components/dashboard/payments-filters-panel";
import { buildPaymentsCsvContent, downloadPaymentsCsvFile, type PaymentCsvRow } from "@/lib/payments-csv-export";
import { useConfirm } from "@/hooks/useConfirm";

const PAGE_SIZE = 20;

type PaymentsItem = {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  startTime: string;
  endTime: string;
  createdAt: string;
  status: AppointmentStatus;
  paymentStatus: PaymentStatus;
  mpPaymentId: string | null;
  service: { id: string; name: string; price: number | null };
  totalPaid: number;
  balance: number | null;
};

type PaymentsApiResponse = {
  aggregates: {
    totalCollected: number;
    totalPending: number;
    totalMpDeposits: number;
    appointmentsWithDebt: number;
  };
  totalCount: number;
  items: PaymentsItem[];
};

type OrgMember = {
  professional: { id: string; name: string; isActive: boolean } | null;
};

function moneyArs(n: number) {
  return `$${n.toFixed(0)}`;
}

function professionalIdQuery(professionalId: string | null): string {
  if (!professionalId) return "";
  return `?professionalId=${encodeURIComponent(professionalId)}`;
}

function buildPaymentsQuery(params: {
  professionalId: string | null;
  dateFrom: string;
  dateTo: string;
  filters: PaymentsTableFilters;
  skip: number;
  take: number;
}): string {
  const q = new URLSearchParams();
  if (params.professionalId) q.set("professionalId", params.professionalId);
  if (params.dateFrom) q.set("dateFrom", params.dateFrom);
  if (params.dateTo) q.set("dateTo", params.dateTo);
  q.set("skip", String(params.skip));
  q.set("take", String(params.take));
  q.set("paid", params.filters.completed ? "1" : "0");
  q.set("partial", params.filters.pending ? "1" : "0");
  q.set("unpaid", params.filters.unpaid ? "1" : "0");
  q.set("refunded", params.filters.refunded ? "1" : "0");
  q.set("cancelled", params.filters.cancelled ? "1" : "0");
  return q.toString();
}

type AppointmentPayLine = {
  id: string;
  amount: number;
  method: string;
  paidAt: string;
  note: string | null;
  mpPaymentId: string | null;
  mpRefundId: string | null;
  refundedAt: string | null;
};

function itemToCsvRow(a: PaymentsItem): PaymentCsvRow {
  return {
    id: a.id,
    createdAt: a.createdAt,
    mpPaymentId: a.mpPaymentId,
    paymentStatus: a.paymentStatus,
    status: a.status,
    service: a.service,
    totalPaid: a.totalPaid,
    balance: a.balance,
  };
}

export function DashboardPayments() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const mpFlash = searchParams.get("mp") ?? undefined;
  const mpAgendaId = searchParams.get("agendaId") ?? undefined;
  const effectiveProfessionalId = searchParams.get("professionalId");

  const [me, setMe] = useState<{ mpConnected: boolean; mpOAuthConfigured: boolean } | null>(null);
  const [data, setData] = useState<PaymentsApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tableFilters, setTableFilters] = useState<PaymentsTableFilters>(DEFAULT_PAYMENTS_TABLE_FILTERS);
  const [page, setPage] = useState(0);

  const [orgProfessionals, setOrgProfessionals] = useState<{ id: string; name: string }[] | null>(null);
  const [canPickProfessional, setCanPickProfessional] = useState(false);

  const [payModalFor, setPayModalFor] = useState<PaymentsItem | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<"CASH" | "TRANSFER" | "OTHER">("CASH");
  const [payNote, setPayNote] = useState("");
  const [payPaidAt, setPayPaidAt] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [payLines, setPayLines] = useState<AppointmentPayLine[] | null>(null);
  const [payLinesLoading, setPayLinesLoading] = useState(false);
  const [refundBusyId, setRefundBusyId] = useState<string | null>(null);

  const skip = page * PAGE_SIZE;

  const load = useCallback(async () => {
    setLoading(true);
    const qs = buildPaymentsQuery({
      professionalId: effectiveProfessionalId,
      dateFrom,
      dateTo,
      filters: tableFilters,
      skip,
      take: PAGE_SIZE,
    });
    const r = await fetchWithSessionSync(`/api/dashboard/payments?${qs}`, { credentials: "include" }, refresh);
    if (r.ok) {
      setData((await r.json()) as PaymentsApiResponse);
    } else {
      setData(null);
    }
    setLoading(false);
  }, [refresh, effectiveProfessionalId, dateFrom, dateTo, tableFilters, skip]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [effectiveProfessionalId, dateFrom, dateTo, tableFilters]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rMe = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
      if (cancelled || !rMe.ok) return;
      const m = (await rMe.json()) as { mpConnected: boolean; mpOAuthConfigured: boolean };
      setMe({ mpConnected: m.mpConnected, mpOAuthConfigured: m.mpOAuthConfigured });
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await fetchWithSessionSync("/api/organizations/me", { credentials: "include" }, refresh);
      if (cancelled || !r.ok) return;
      const j = (await r.json()) as { organization: { id: string } | null; role: string | null };
      const role = j.role;
      const can = role === "owner" || role === "admin";
      setCanPickProfessional(Boolean(j.organization && can));
      if (!j.organization || !can) {
        setOrgProfessionals(null);
        return;
      }
      const rm = await fetchWithSessionSync(`/api/organizations/${j.organization.id}/members`, { credentials: "include" }, refresh);
      if (cancelled || !rm.ok) return;
      const { members } = (await rm.json()) as { members: OrgMember[] };
      const rows = (members ?? [])
        .map((m) => m.professional)
        .filter((p): p is { id: string; name: string; isActive: boolean } => Boolean(p?.id && p.isActive))
        .map((p) => ({ id: p.id, name: p.name }));
      setOrgProfessionals(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    if (!payModalFor) {
      setPayLines(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      setPayLinesLoading(true);
      const q = professionalIdQuery(effectiveProfessionalId);
      const r = await fetchWithSessionSync(
        `/api/dashboard/appointments/${encodeURIComponent(payModalFor.id)}/payments${q}`,
        { credentials: "include" },
        refresh,
      );
      if (cancelled) return;
      if (r.ok) {
        const j = (await r.json()) as { payments: AppointmentPayLine[] };
        setPayLines(j.payments ?? []);
      } else {
        setPayLines([]);
      }
      setPayLinesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [payModalFor, effectiveProfessionalId, refresh]);

  async function exportCsv() {
    const qs = buildPaymentsQuery({
      professionalId: effectiveProfessionalId,
      dateFrom,
      dateTo,
      filters: tableFilters,
      skip: 0,
      take: 5000,
    });
    const r = await fetchWithSessionSync(`/api/dashboard/payments?${qs}`, { credentials: "include" }, refresh);
    if (!r.ok) return;
    const j = (await r.json()) as PaymentsApiResponse;
    const rows = j.items.map(itemToCsvRow);
    const content = buildPaymentsCsvContent(rows);
    const name = `cobros-${format(new Date(), "yyyy-MM-dd")}.csv`;
    downloadPaymentsCsvFile(name, content);
  }

  const totalPages = useMemo(() => {
    if (!data) return 0;
    return Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));
  }, [data]);

  function setProfessionalFilter(id: string | null) {
    const base = "/dashboard/payments";
    if (!id) navigate(base);
    else navigate(`${base}?professionalId=${encodeURIComponent(id)}`);
  }

  async function submitManualPayment() {
    if (!payModalFor) return;
    const amount = Number(String(payAmount).replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setPayError("Ingresá un importe válido.");
      return;
    }
    setPayError(null);
    setPaySaving(true);
    try {
      const body: Record<string, unknown> = {
        amount,
        method: payMethod,
        note: payNote.trim() || null,
      };
      if (payPaidAt.trim()) {
        const d = new Date(payPaidAt);
        if (!Number.isNaN(d.getTime())) body.paidAt = d.toISOString();
      }
      const q = professionalIdQuery(effectiveProfessionalId);
      const r = await fetchWithSessionSync(
        `/api/dashboard/appointments/${encodeURIComponent(payModalFor.id)}/payments${q}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setPayError(j.error ?? "No se pudo registrar el cobro.");
        return;
      }
      setPayModalFor(null);
      setPayAmount("");
      setPayNote("");
      setPayPaidAt("");
      await load();
    } finally {
      setPaySaving(false);
    }
  }

  async function refundMercadoPagoLine(paymentRowId: string) {
    if (
      !(await confirm(
        "¿Devolver el dinero al cliente mediante Mercado Pago? El reembolso se procesa en Mercado Pago; la comisión de plataforma ya descontada no se recupera automáticamente.",
      ))
    ) {
      return;
    }
    setRefundBusyId(paymentRowId);
    setPayError(null);
    try {
      const q = professionalIdQuery(effectiveProfessionalId);
      const r = await fetchWithSessionSync(
        `/api/dashboard/payments/${encodeURIComponent(paymentRowId)}/refund${q}`,
        { method: "POST", credentials: "include" },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setPayError(j.error ?? "No se pudo procesar la devolución.");
        return;
      }
      await load();
      if (payModalFor) {
        const r2 = await fetchWithSessionSync(
          `/api/dashboard/appointments/${encodeURIComponent(payModalFor.id)}/payments${q}`,
          { credentials: "include" },
          refresh,
        );
        if (r2.ok) {
          const j2 = (await r2.json()) as { payments: AppointmentPayLine[] };
          setPayLines(j2.payments ?? []);
        }
      }
    } finally {
      setRefundBusyId(null);
    }
  }

  if (!me) {
    return (
      <DashboardPageShell eyebrow="Cobros" title="Cobros">
        <LoadingFrame variant="light" minHeight={300} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Cobros"
      title="Cobros"
      description="Turnos con saldo, cobros parciales y movimientos. Los importes son en pesos (ARS)."
    >
      <MercadoPagoForm mpFlash={mpFlash} mpAgendaId={mpAgendaId} />

      <CancellationPolicyForm />

      {canPickProfessional && orgProfessionals && orgProfessionals.length > 0 ? (
        <div className={css({ mb: "4" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm", color: "#64748b", maxW: "320px" })}>
            Profesional
            <select
              value={effectiveProfessionalId ?? ""}
              onChange={(e) => setProfessionalFilter(e.target.value || null)}
              className={css({
                py: "2",
                px: "3",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                fontSize: "sm",
                bg: "white",
              })}
            >
              <option value="">Yo</option>
              {orgProfessionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {loading && !data ? (
        <LoadingFrame variant="light" minHeight={280} />
      ) : !data ? (
        <p className={css({ color: "#64748b", fontSize: "sm" })}>No se pudo cargar la lista de cobros.</p>
      ) : data.totalCount === 0 ? (
        <DashboardEmpty
          title="Sin turnos en esta vista"
          hint="Ajustá filtros o registrá un cobro desde el calendario. Los turnos sin pagar recientes o futuros aparecen acá."
        />
      ) : (
        <>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
              gap: "3",
              mb: "4",
            })}
          >
            <AggregateCard label="Cobrado (vista / fechas)" value={moneyArs(data.aggregates.totalCollected)} />
            <AggregateCard label="Pendiente (saldo servicio)" value={moneyArs(data.aggregates.totalPending)} />
            <AggregateCard label="Señas Mercado Pago" value={moneyArs(data.aggregates.totalMpDeposits)} />
            <AggregateCard label="Turnos con deuda" value={String(data.aggregates.appointmentsWithDebt)} />
          </div>

          <PaymentsFiltersPanel
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            filters={tableFilters}
            onFiltersChange={setTableFilters}
            headerExtra={
              <Button
                type="button"
                variant="secondary"
                onClick={exportCsv}
                disabled={data.items.length === 0}
                className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}
              >
                <Download size={18} strokeWidth={2} aria-hidden />
                Exportar
              </Button>
            }
          />

          <div className={cx(cf.dashSurface, css({ overflow: "hidden" }))}>
            <div className={css({ overflowX: "auto" })}>
              <table className={css({ w: "full", minW: "720px", borderCollapse: "collapse", fontSize: "sm" })}>
                <thead>
                  <tr className={css({ bg: "#f8fafc", textAlign: "left" })}>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Cliente / servicio</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Turno</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Estado</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Pago</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Cobrado</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })}>Saldo</th>
                    <th className={css({ py: "3", px: "4", color: "#64748b", fontWeight: "semibold" })} />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((a) => (
                    <tr key={a.id} className={css({ borderTopWidth: "1px", borderColor: "#e2e8f0" })}>
                      <td className={css({ py: "3", px: "4", color: "#0f172a" })}>
                        <span className={css({ fontWeight: "semibold" })}>{a.clientName}</span>
                        <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "1" })}>{a.service?.name ?? "—"}</span>
                      </td>
                      <td className={css({ py: "3", px: "4", color: "#64748b", whiteSpace: "nowrap" })}>
                        {format(new Date(a.startTime), "d MMM yyyy HH:mm", { locale: es })}
                      </td>
                      <td className={css({ py: "3", px: "4", color: "#334155" })}>{labelAppointmentStatus(a.status)}</td>
                      <td className={css({ py: "3", px: "4", color: "#334155" })}>{labelPaymentStatus(a.paymentStatus)}</td>
                      <td className={css({ py: "3", px: "4", color: "#0f172a" })}>{moneyArs(a.totalPaid)}</td>
                      <td className={css({ py: "3", px: "4", color: "#64748b" })}>
                        {a.balance != null ? moneyArs(a.balance) : "—"}
                      </td>
                      <td className={css({ py: "3", px: "4" })}>
                        {a.status !== "CANCELLED" ? (
                          <Button type="button" variant="secondary" onClick={() => setPayModalFor(a)}>
                            Cobrar
                          </Button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", alignItems: "center", mt: "4", fontSize: "sm", color: "#64748b" })}>
            <span>
              Página {page + 1} de {totalPages} · {data.totalCount} turnos
            </span>
            <Button type="button" variant="secondary" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Anterior
            </Button>
            <Button type="button" variant="secondary" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Siguiente
            </Button>
          </div>
        </>
      )}

      {payModalFor ? (
        <div
          className={css({
            position: "fixed",
            inset: 0,
            zIndex: 70,
            bg: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: "4",
          })}
          onClick={(e) => e.target === e.currentTarget && !paySaving && setPayModalFor(null)}
        >
          <div
            className={css({
              bg: "white",
              rounded: "xl",
              maxW: "420px",
              w: "100%",
              p: "6",
              boxShadow: "0 20px 50px rgba(15,23,42,0.15)",
            })}
          >
            <h3 className={css({ fontSize: "lg", fontWeight: "bold", color: "#0f172a", mb: "1" })}>Registrar cobro</h3>
            <p className={css({ fontSize: "sm", color: "#64748b", mb: "4" })}>
              {payModalFor.clientName} · {payModalFor.service?.name ?? "—"}
            </p>
            {payLinesLoading ? (
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "3" })}>Cargando cobros del turno…</p>
            ) : payLines && payLines.length > 0 ? (
              <div className={css({ mb: "4", p: "3", rounded: "lg", bg: "#f8fafc", borderWidth: "1px", borderColor: "#e2e8f0" })}>
                <p className={css({ fontSize: "xs", fontWeight: "semibold", color: "#64748b", mb: "2" })}>Cobros registrados</p>
                <ul className={css({ listStyle: "none", m: 0, p: 0, display: "flex", flexDir: "column", gap: "2" })}>
                  {payLines.map((line) => {
                    const canRefund =
                      line.method === "MERCADOPAGO" && line.mpPaymentId && !line.refundedAt;
                    return (
                      <li
                        key={line.id}
                        className={css({
                          fontSize: "xs",
                          color: "#334155",
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "2",
                          justifyContent: "space-between",
                        })}
                      >
                        <span>
                          {moneyArs(line.amount)} · {line.method}
                          {line.refundedAt ? (
                            <span className={css({ color: "#b45309", ml: "2" })}> · Devuelto</span>
                          ) : null}
                        </span>
                        {canRefund ? (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={refundBusyId != null}
                            onClick={() => void refundMercadoPagoLine(line.id)}
                            className={css({ fontSize: "xs", py: "1", px: "2" })}
                          >
                            {refundBusyId === line.id ? "Procesando…" : "Devolver en MP"}
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {payError ? (
              <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "3" })}>{payError}</p>
            ) : null}
            <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
              <label className={css({ fontSize: "sm", color: "#475569" })}>
                Importe (ARS)
                <Input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0" className={css({ mt: "1" })} />
              </label>
              <label className={css({ fontSize: "sm", color: "#475569" })}>
                Medio
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value as "CASH" | "TRANSFER" | "OTHER")}
                  className={css({
                    mt: "1",
                    w: "100%",
                    py: "2",
                    px: "3",
                    rounded: "lg",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                  })}
                >
                  <option value="CASH">Efectivo</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="OTHER">Otro</option>
                </select>
              </label>
              <label className={css({ fontSize: "sm", color: "#475569" })}>
                Nota (opcional)
                <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} className={css({ mt: "1" })} />
              </label>
              <label className={css({ fontSize: "sm", color: "#475569" })}>
                Fecha del cobro (opcional, default hoy)
                <Input type="datetime-local" value={payPaidAt} onChange={(e) => setPayPaidAt(e.target.value)} className={css({ mt: "1" })} />
              </label>
            </div>
            <div className={css({ display: "flex", gap: "2", mt: "5", justifyContent: "flex-end" })}>
              <Button type="button" variant="secondary" disabled={paySaving} onClick={() => setPayModalFor(null)}>
                Cancelar
              </Button>
              <Button type="button" disabled={paySaving} onClick={() => void submitManualPayment()}>
                {paySaving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardPageShell>
  );
}

function AggregateCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      className={cx(
        cf.dashSurface,
        css({
          py: "3",
          px: "4",
          display: "flex",
          flexDir: "column",
          gap: "1",
        }),
      )}
    >
      <span className={css({ fontSize: "xs", color: "#64748b", fontWeight: "semibold" })}>{label}</span>
      <span className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a" })}>{value}</span>
    </div>
  );
}
